import $ from 'jquery';
import Texter from "./texter.es6";
import Modaller from "./modaller";
import ProgressButton from "./progressButton";
import {Numbers} from "./numberFormatter.es6";
import {aPromise} from "./jqUtil.ts";
import CardSideAdapter from "./cardSideAdapter.es6";
import {shuffleArray, noop} from "./playUtil.es6";

require('jsrender')($);

const log = console.log;
// const log = noop;

const START_DIALOG = $.templates(`
<div class="text-center">
    <h2 class="my-3 text-primary">{{>~l18n.get('packPlay.head.match')}}</h2>
    <div class="my-3">{{>~l18n.get('fl.matchGame.welcome.text')}}</div>
    <div class="form-group row">
        <label for="flMatchGameCardCount" class="col-sm-6 col-form-label">{{>~l18n.get('fl.matchGame.preset.number')}}:</label>
        <div class="col-sm-6"><input type="number" class="form-control" id="flMatchGameCardCount" value="{{:cardCount}}" min="1" max="{{:cardMax}}"></div>
    </div>
    <button type="button" class="btn btn-primary" data-click="start">{{>~l18n.get('fl.matchGame.startGame')}}</button>
</div>
`);

const RESULT_DIALOG = $.templates(`
<div class="text-center">
    <h2 class="my-3 text-primary">{{>~l18n.get('fl.matchGame.result.title')}}</h2>
    <div class="my-3">{{>~l18n.get('fl.matchGame.result.text', time)}}</div>
    <button type="button" class="btn btn-primary" data-click="restart">{{>~l18n.get('fl.matchGame.result.again')}}</button>
</div>
`);

const TIMER = $.templates(`
<div class="text-center fl-match-game__timer py-1">
    <div class="fl-match-game__timer-label">{{>~l18n.get('fl.matchGame.timer')}}</div>
    <div data-bind="gameTime"></div>
</div>
`);

const SIDE = $.templates(`
<div class="fl-match-game__side" id="{{:id}}">
    {{if image}}<div class="fl-match-game__side-image"><img src="{{attr:image}}"></div>{{/if}}
    {{if text}}<div class="fl-tt-{{:textType}}">{{if textType == 'html'}}{{:text}}{{else}}{{>text}}{{/if}}</div>{{/if}}
</div>
`);

class Vector {
    constructor(x = 0, y = 0) {
        this.x = x;
        this.y = y;
    }

    copy() {
        return new Vector(this.x, this.y);
    }

    addSelf(dx, dy) {
        this.x += dx;
        this.y += dy;
    }

    addSelfV(v) {
        this.x += v.x;
        this.y += v.y;
    }

    addV(v) {
        return new Vector(this.x + v.x, this.y + v.y);
    }

    multSelf(sx, sy) {
        this.x *= sx;
        this.y *= sy;
    }

    mult(sx, sy) {
        return new Vector(this.x * sx, this.y * sy);
    }

    zeroSelf() {
        this.x = this.y = 0;
    }

    isZero() {
        return this.x === 0 && this.y === 0;
    }

    /**
     * @param {Vector} other
     */
    subtract(other) {
        return new Vector(this.x - other.x, this.y - other.y);
    }

    normalizeSelf() {
        const length = Math.sqrt(this.x * this.x + this.y * this.y);
        if (length < 1e-4) {
            return;
        }
        this.x /= length;
        this.y /= length;
    }
}

class Rect {
    constructor(position = new Vector(), width = 0, height = 0) {
        this.position = position;
        this.width = width;
        this.height = height;
    }

    get left() {
        return this.position.x - this.width / 2;
    }

    get right() {
        return this.position.x + this.width / 2;
    }

    get top() {
        return this.position.y - this.height / 2;
    }

    get bottom() {
        return this.position.y + this.height / 2;
    }

    hit(x, y) {
        return x >= this.left && x <= this.right
            && y >= this.top && y <= this.bottom;
    }

    /**
     * @param {Rect} rect
     */
    intersect(rect) {
        return rect.left <= this.right && rect.right >= this.left
            && rect.top <= this.bottom && rect.bottom >= this.top;
    }

    /**
     * https://forum.sources.ru/index.php?showtopic=141648&view=findpost&p=1096882
     *
     * @param {Rect} other
     */
    distanceTo(other) {
        let xoverlap = 0;
        if (this.right < other.left) {
            xoverlap = other.left - this.right;
        }
        if (this.left > other.right) {
            xoverlap = this.left - other.right;
        }

        let yoverlap = 0;
        if (this.bottom < other.top) {
            yoverlap = other.top - this.bottom;
        }
        if (this.top > other.bottom) {
            yoverlap = this.top - other.bottom;
        }

        if (xoverlap === 0 || yoverlap === 0) {
            return Math.max(xoverlap, yoverlap);
        }
        return Math.sqrt(xoverlap * xoverlap + yoverlap * yoverlap);
    }
}

class FieldSide {
    constructor(card, elementId) {
        this.card = card;
        this.elementId = elementId;
        this.el_ = null;
        this.rect = null;
    }

    get element() {
        if (this.el_ === null) {
            this.el_ = document.getElementById(this.elementId);
        }
        return this.el_;
    }

    hit(clientX, clientY) {
        return clientX >= this.rect.left && clientX <= this.rect.right
            && clientY >= this.rect.top && clientY <= this.rect.bottom;
    }

    intersect(rect) {
        return rect.left <= this.rect.right && rect.right >= this.rect.left
            && rect.top <= this.rect.bottom && rect.bottom >= this.rect.top;
    }

    updateRect() {
        this.rect = this.element.getBoundingClientRect();
    }

    get size() {
        return this.rect.width * this.rect.height;
    }
}

class Dragger {
    constructor({start, done, elementsUnderPoint}) {
        this.clientX = null;
        this.clientY = null;
        this.target = null;
        this.under = null;

        this.cancelFunc = this.cancel.bind(this);
        this.moveFunc = this.move.bind(this);
        this.elementsUnderPoint = elementsUnderPoint;
        this.elementStart = start;
        this.elementDone = done;
    }

    _reset() {
        this.clientX = null;
        this.clientY = null;

        if (this.target != null) {
            this.elementDone(this.target, this.under);
            this.target = null;
        }

        this._removeUnder();
    }

    getLastClientXY(e) {
        //если касание, то вычисляем через event.originalEvent.touches[0]:
        if (/touch/.test(e.type)) {
            const touch = e.originalEvent.touches[0] || e.originalEvent.changedTouches[0];
            this.lastX = touch.clientX;
            this.lastY = touch.clientY;
        } else {
            //если нажата кнопка мышки:
            this.lastX = e.clientX;
            this.lastY = e.clientY;
        }
    }

    start(e) {
        e.preventDefault();
        this.getLastClientXY(e);

        this.target = e.currentTarget;
        this.under = null;
        // get the mouse cursor position at startup:
        this.clientX = this.lastX;
        this.clientY = this.lastY;

        $(document)
            .on('touchend touchcancel mouseup', this.cancelFunc)
            .on('touchmove mousemove', this.moveFunc);

        log(`drag start for #${this.target.id}`);
        this.elementStart(this.target);
    }

    cancel() {
        log('drag stop');

        $(document)
            .off('touchend touchcancel mouseup', this.cancelFunc)
            .off('touchmove mousemove', this.moveFunc);

        this._reset();
    }

    move(e) {
        if (this.target == null) {
            return;
        }

        e.preventDefault();
        this.getLastClientXY(e);

        // calculate the new cursor position:
        const pos1 = this.clientX - this.lastX;
        const pos2 = this.clientY - this.lastY;
        this.clientX = this.lastX;
        this.clientY = this.lastY;

        // set the element's new position:
        this.target.style.top = (this.target.offsetTop - pos2) + "px";
        this.target.style.left = (this.target.offsetLeft - pos1) + "px";

        const underList = this.elementsUnderPoint(this.clientX, this.clientY)
            .filter(item => item.id !== this.target.id);
        this._changeUnder(underList.length ? underList[underList.length - 1] : null);
    }

    _changeUnder(newUnder) {
        if (this.under != null) {
            if (newUnder != null && this.under.id === newUnder.id) {
                // same element
                return;
            }
            this._removeUnder();
        }

        if (newUnder != null && newUnder.id !== this.target.id) {
            this.under = newUnder;
            $(this.under).addClass('fl-match-game__side--over');
        }
    }

    _removeUnder() {
        if (this.under != null) {
            $(this.under).removeClass('fl-match-game__side--over');
            this.under = null;
        }
    }
}

class MatchPlay {
    constructor({l18n, packId, gameField, gameTime, cardCount, cardMax}) {
        this.l18n = Texter.create(l18n);
        this.packId = packId;
        this.gameField = gameField;

        this.cardCount = cardCount;
        this.cardMax = cardMax;

        this.gameTime = gameTime;
        this.gameTimeFieldText = null;

        this.startTime = null;
        this.startTimeProc = 0;

        this.dragger = new Dragger({
            start: el => {
                $(el).addClass('fl-match-game__side--moving');
                el.style.zIndex = 100;
            },
            done: (el, under) => {
                const elJq = $(el);
                elJq.removeClass('fl-match-game__side--moving');
                el.style.zIndex = '';

                const elSide = this._sideByElement(el);
                this._pushToFront(elSide);

                /* это если надо вернуть элемент на старое место, если не подошло
                this._moveBackToPosition(elSide)
                 */
                elSide.updateRect();

                if (under != null) {
                    const underSide = this._sideByElement(under);
                    if (underSide.card === elSide.card) {
                        this._removeSide(elSide);
                        this._removeSide(underSide);
                    }
                }
            },

            elementsUnderPoint: (clientX, clientY) => {
                // log(`hit test {${clientX},${clientY}}`);
                return this.sides
                    .filter(item => item.hit(clientX, clientY))
                    .map(item => item.element);
            }
        });
        this.gameField.on('touchstart mousedown', 'div.fl-match-game__side', e => {
            this.dragger.start(e);
        });
    }

    _pushToFront(side) {
        const jqElement = $(side.element);

        // push it to front
        jqElement.remove();
        this.gameField.append(jqElement);

        const index = this.sides.indexOf(side);
        this.sides.splice(index, 1);
        this.sides.push(side);
    }

    _sideByElement(element) {
        return this.sidesByElementId[element.id];
    }

    _removeSide(side) {
        delete this.sidesByElementId[side.element.id];
        this.sides = this.sides.filter(i => i !== side);

        const element = $(side.element);
        element.fadeOut('fast', () => element.remove());

        if (this.sides.length === 0) {
            this._stopGame();
        }
    }

    start() {
        const html = START_DIALOG.render({
            cardCount: this.cardCount,
            cardMax: this.cardMax
        }, this);

        Modaller.showLocal(html, modaller => {
            modaller.center();
            modaller.disableHide();
            modaller.addDialogClass('modal-sm');
            modaller.content.find('button[data-click=start]').click(e => {
                new ProgressButton(e.currentTarget).progress = true;

                this.cardCount = parseInt(modaller.content.find('input#flMatchGameCardCount').val());
                if (Numbers.isNumber(this.cardCount)) {
                    this.cardCount = Math.min(this.cardMax, this.cardCount);
                } else {
                    this.cardCount = Math.min(this.cardMax, 4);
                }

                this._startGame(modaller);
            });
            modaller.addShowListener(() => {
                modaller.content.find('button[data-click=start]').focus();
            });
        });
    }

    _startGame(modaller) {
        aPromise($.get(`${Flashcards.root}/set/${this.packId}/cards-random.ajax?limit=${this.cardCount}`))
            .then(cards => this._addCards(cards))
            .then(cards => {
                const next = this._arrange();
                modaller.close();
                return next;
            })
            .then(() => {
                this.gameTime
                    .empty()
                    .append(TIMER.render({}, this));

                this.gameTimeField = this.gameTime.find('[data-bind="gameTime"]');
                this.gameTimeFieldText = null;
                this.startTime = (new Date).getTime();
                this._updateTime();
                this.startTimeProc = setInterval(() => {
                    this._updateTime();
                }, 50);
            });
    }

    _stopGame() {
        clearInterval(this.startTimeProc);

        const html = RESULT_DIALOG.render({
            time: this.durationText
        }, this);

        Modaller.showLocal(html, modaller => {
            modaller.center();
            modaller.disableHide();
            modaller.addDialogClass('modal-sm');
            modaller.content.find('button[data-click=restart]').click(() => {
                modaller.close();
                this.start();
            });
            modaller.addShowListener(() => {
                modaller.content.find('button[data-click=restart]').focus();
            });
        });

    }

    /**
     * @param {Array} cards
     * @returns {Promise}
     * @private
     */
    _addCards(cards) {
        this.sides = []; // список всех сторон
        this.sidesByElementId = {}; // html tag id -> side

        const cardSideAdapter = new CardSideAdapter(true);

        let totalHtml = '';

        const addSide = (card, questionModel, sideMark) => {
            const elementId = `c_${card.id}${sideMark}`;
            totalHtml += SIDE.render({
                ...questionModel,
                id: elementId
            }, this);

            const side = new FieldSide(card.id, elementId);
            this.sides.push(side);
            this.sidesByElementId[elementId] = side;
        };

        cards.forEach(card => {
            addSide(card, cardSideAdapter.createQuestion(card), 'face');
            addSide(card, cardSideAdapter.createAnswer(card), 'back');
        });

        log('append html');
        this.gameField.append(totalHtml);

        // wait all images to complete
        log('wait all images to complete');

        const waitImages = [];
        this.gameField.find('img').each(function () {
            const img = this;
            if (!img.complete) {
                waitImages.push(new Promise(function (resolve) {
                    img.addEventListener('load', resolve);
                    img.addEventListener('error', resolve);
                }));
            }
        });

        return Promise.all(waitImages);
    }

    _autosizeCards(area) {
        const maxWidth = area.width / 4;

        // обновляем размеры
        this.sides.forEach(item => {
            item.updateRect();

            const w = item.rect.width, h = item.rect.height;
            if (w > maxWidth) {
                $(item.element).width(maxWidth);
                item.updateRect();
            }
        });
    }

    // раскидаем равномерно, примерно
    /**
     * @returns {Promise}
     * @private
     */
    _arrange() {
        const area = {
            width: this.gameField.width(),
            height: this.gameField.height()
        };
        area.left = (-area.width / 2) * .9;
        area.right = (area.width / 2) * .9;
        area.top = (-area.height / 2) * .9;
        area.bottom = (area.height / 2) * .9;

        this._autosizeCards(area);

        // взорвем их!!
        // 1. располагаем все квадратики по сетке с шагом 10.
        // 2. даем им всем пенделя (заряд в 1 Кл)
        // 3. задаем граничные условия
        //      a) объекты не пересекаются - отталкивание равно 0.
        //      b) если пересекаются - отталкивание равно 1
        //      c) есть сила трения - если отталкивания нет, то скорость падает.
        //      z) границы поля не отталкивают. но и проходить не дают.
        // 4. Теперь с некоторым шагом делаем метод конечных элементов
        // 5. Условие конца:
        //      a) превышено кол-во шагов в эмуляции
        //      b) если никто уже не пересекается
        let electrons = this.sides.map(item => {
            return {
                velocity: new Vector(),
                rect: new Rect(new Vector(), item.rect.width, item.rect.height),
                item: item
            };
        });

        // делаем начальную сетку
        const gridSize = Math.ceil(Math.sqrt(electrons.length));
        const gridGap = 2;
        shuffleArray(electrons);
        electrons.forEach((e, index) => {
            e.rect.position.addSelf(index % gridSize, Math.floor(index / gridSize));
            e.rect.position.addSelf(-(gridSize - 1) / 2, -(gridSize - 1) / 2);
            e.rect.position.multSelf(gridGap, gridGap);
            // немного рандома, на полпикселя
            e.rect.position.addSelf(Math.random() - .5, Math.random() - .5);
        });

        // показываем первоначальные позиции
        electrons.forEach(e => {
            $(e.item.element).css({
                left: area.width / 2 + e.rect.left,
                top: area.height / 2 + e.rect.top
            });
        });

        // ок, погнали
        let femSteps = 0;
        let femTimeStart = (new Date).getTime();
        let femTimeLimit = 1000; // 1 sec
        let femDebug = false;
        const knobbleThreshold = 5;

        function femComplete(resolve) {
            log('complete fem');

            electrons.forEach(e => {
                $(e.item.element).animate({
                    left: area.width / 2 + e.rect.left,
                    top: area.height / 2 + e.rect.top
                }, 500, 'swing', () => e.item.updateRect());
            });

            const now = (new Date).getTime();
            log(`femmed in ${femSteps} steps (${now - femTimeStart} ms)`);

            $(":animated").promise().done(() => {
                log('fem animated');
                resolve();
            });
        }

        function femStep() {
            const now = (new Date).getTime();
            if (now - femTimeStart > femTimeLimit) {
                return false;
            }

            femSteps++;
            electrons.forEach(e => e.velocity.zeroSelf());

            // обнуляем скорости
            // отталкиваем всех со всеми. увеличиваем скорость, если пересекаются.
            // а если не пересекается, то смотрим дистанцию. если все еще близко - то на пол-шишечки двигаемся
            electrons.forEach((e, index) => {
                for (let otherIndex = index + 1; otherIndex < electrons.length; otherIndex++) {
                    const other = electrons[otherIndex];
                    let force = null;

                    // валим на полную
                    if (e.rect.intersect(other.rect)) {
                        force = e.rect.position.subtract(other.rect.position);
                        force.normalizeSelf();
                    }
                    // или валим на полшишечки
                    else if (e.rect.distanceTo(other.rect) < knobbleThreshold) {
                        force = e.rect.position.subtract(other.rect.position);
                        force.normalizeSelf();
                        force.multSelf(.5, .5);
                    }

                    if (force != null) {
                        e.velocity.addSelfV(force);
                        other.velocity.addSelfV(force.mult(-1, -1));
                    }
                }
            });

            // скорости назначили. двигаем
            let electronsMoved = false;
            electrons.forEach(e => {
                if (e.velocity.isZero()) {
                    return;
                }

                const oldPosition = e.rect.position;
                e.rect.position = e.rect.position.addV(e.velocity);

                // если пересекаем какую-то границу, то дальше не идем
                let vdX = false;
                if (e.rect.bottom > area.bottom || e.rect.top < area.top) {
                    e.velocity.y = 0;
                    vdX = true;
                }

                let vdY = false;
                if (e.rect.right > area.right || e.rect.left < area.left) {
                    e.velocity.x = 0;
                    vdY = true;
                }

                if (vdX || vdY) {
                    e.rect.position = oldPosition;
                    e.rect.position.addSelfV(e.velocity);
                }

                electronsMoved = true;
            });

            if (!electronsMoved) {
                return false;
            }

            if (femDebug) {
                // анимируем результат
                electrons.forEach(e => {
                    $(e.item.element).css({
                        left: area.width / 2 + e.rect.left,
                        top: area.height / 2 + e.rect.top
                    });
                });
            }

            return true;
        }

        log('start fem');
        return new Promise(resolve => {
            if (femDebug) {
                femTimeLimit *= 100;
                let femProcedureTimer = setInterval(femTimerProc, 100);

                function femTimerProc() {
                    if (!femStep()) {
                        femComplete(resolve);
                        clearInterval(femProcedureTimer);
                    }
                }
            } else {
                while (femStep()) {
                }
                femComplete(resolve);
            }
        });
    }

    get duration() {
        return ((new Date).getTime() - this.startTime) / 1000;
    }

    get durationText() {
        let durationText;
        const seconds = this.duration;
        if (seconds >= 10) {
            durationText = Math.floor(seconds) + '';
        } else {
            durationText = Numbers.toDecimals(seconds, 1);
        }
        return durationText;
    }

    _updateTime() {
        const durationText = this.durationText;
        if (this.gameTimeFieldText !== durationText) {
            this.gameTimeFieldText = durationText;
            this.gameTimeField.text(durationText + 's');
        }
    }

    _moveBackToPosition(side) {
        const rect = side.rect;
        const offset = this.gameField.offset();
        $(side.element).animate({
            top: rect.top - offset.top,
            left: rect.left - offset.left
        }, 200);
    }
}

global.MatchPlay = MatchPlay;